/**
 * IndieChatInput
 * Sticky input bar at the bottom of the chat panel.
 * Pixel-faithful port from Figma export (fusion-starter-6c1/client/pages/Index.tsx → ChatPanel)
 */
import React, { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react';
import { Paperclip, ArrowUp, ArrowUpRight, X, Bookmark } from 'lucide-react';
import type { OrbitChatUpload } from '../../services/OrbitChatService';

interface Props {
  onSend: (text: string) => void;
  onAttachFile?: () => void | Promise<void>;
  attachedUploads?: OrbitChatUpload[];
  onRemoveUpload?: (index: number) => void;
  placeholder?: string;
  disabled?: boolean;
  /** True while an SSE stream is in progress */
  isStreaming?: boolean;
  surface?: 'default' | 'drawer';
  onOpenFullView?: () => void;
  onOpenPrompts?: () => void;
  initialValue?: string;
}

const IndieChatInput: React.FC<Props> = ({
  onSend,
  onAttachFile,
  attachedUploads = [],
  onRemoveUpload,
  placeholder = 'Ask anything about your store…',
  disabled = false,
  isStreaming = false,
  surface = 'default',
  onOpenFullView,
  onOpenPrompts,
  initialValue,
}) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const appliedInitialValueRef = useRef<string | undefined>();
  const isBlocked = disabled || isStreaming;
  const isDrawerSurface = surface === 'drawer';

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const maxHeight = 144;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, maxHeight)}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeInput();
  }, [resizeInput, value]);

  useEffect(() => {
    if (!initialValue || initialValue === appliedInitialValueRef.current) return;
    appliedInitialValueRef.current = initialValue;
    setValue(initialValue);
    inputRef.current?.focus();
  }, [initialValue]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isBlocked) return;
    onSend(trimmed);
    setValue('');
  };

  const handleAttachClick = async () => {
    if (!onAttachFile || isBlocked) return;
    await onAttachFile();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`indie-chat flex-shrink-0 ${isDrawerSurface ? 'px-4 pb-3 pt-2 bg-white' : 'px-6 pb-4 pt-3'}`}>
      <div className="flex flex-col gap-2">

        {/* ── Thinking status row ── */}
        <div
          className={`flex items-center gap-2 px-1 overflow-hidden transition-all duration-300 ${isStreaming ? 'max-h-6 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
            }`}
          aria-live="polite"
          aria-atomic="true"
        >
          {/* Animated dots */}
          <span className="flex items-center gap-[3px]">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-[5px] h-[5px] rounded-full bg-[#6366F1]"
                style={{
                  animation: 'indie-typing-dot 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.18}s`,
                }}
              />
            ))}
          </span>
          <span className="text-[11px] text-[#6366F1] font-medium tracking-wide">
            Indie is thinking…
          </span>
        </div>

        {/* Input row */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors ${isStreaming ? 'border-[#DDD6FE] bg-[#F9FAFB]' : 'border-[#EAECF0] bg-[#F9FAFB] focus-within:border-[#D0D5DD] focus-within:bg-white'
          }`}>
          {/* Text */}
          <div className="flex-1 min-w-0">
            <textarea
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={isStreaming ? 'Indie is thinking…' : placeholder}
              disabled={isBlocked}
              aria-label="Ask Indie"
              className={`block w-full resize-none bg-transparent font-normal outline-none disabled:opacity-50 ${isDrawerSurface
                ? 'text-[#101828] text-[13px] leading-[18px] placeholder:text-[#98A2B3]'
                : 'text-[#333333] text-[13px] leading-[20.8px] tracking-[-0.076px] placeholder:text-[#667085]'
                }`}
            />
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              aria-label="Attach file"
              onClick={() => { void handleAttachClick(); }}
              disabled={isBlocked}
              className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#EAECF0] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Paperclip className="w-4 h-4 text-[#98A2B3] hover:text-[#667085]" />
            </button>
            {isDrawerSurface && (
              <button
                type="button"
                aria-label="Prompts"
                onClick={onOpenPrompts}
                disabled={isBlocked}
                className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#EAECF0] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Bookmark className="w-4 h-4 text-[#98A2B3] hover:text-[#667085]" />
              </button>
            )}
          </div>

          {/* Send */}
          <button
            type="button"
            onClick={handleSend}
            disabled={!value.trim() || isBlocked}
            aria-label={isStreaming ? 'Waiting for response' : 'Send message'}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-white shadow-none"
          >
            {isStreaming ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-white" />
            )}
          </button>
        </div>

        {attachedUploads.length > 0 && (
          <div className="flex flex-wrap gap-2 px-1">
            {attachedUploads.map((upload, index) => (
              <span
                key={`${upload.filename}-${index}`}
                className="inline-flex items-center gap-1 rounded-full border border-[#DDD6FE] bg-[#F9F5FF] px-2 py-1 text-[10px] font-medium text-[#53389E]"
              >
                {upload.filename}
                {onRemoveUpload && (
                  <button
                    type="button"
                    aria-label={`Remove ${upload.filename} from chat context`}
                    onClick={() => onRemoveUpload(index)}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-[#E9D7FE] transition-colors"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {isDrawerSurface ? (
          <p className="text-[#98A2B3] text-[11px] font-normal leading-[15px] text-center mt-0.5">
            Indie can make mistakes &mdash; verify key numbers before acting
          </p>
        ) : (
          <p className="text-[#98A2B3] text-[10px] font-normal leading-[15px] tracking-[0.117px] text-center">
            AI can make mistakes &mdash; always verify key numbers before acting
          </p>
        )}
      </div>
    </div>
  );
};

export default IndieChatInput;